import React, { useState, useEffect } from "react";
import { Flex, Text, Button, Switch, RadioCards, Slider } from "@radix-ui/themes";
import { COMPETENCY_COLORS } from "../../../utils/constants";
import aiService from "../../../services/aiService";

const AIQuestionsSettings = ({course, topics, onCancel, onComplete, exclude}) => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [useRecommended, setUseRecommended] = useState(true);
    const [ratios, setRatios] = useState({vocabulary: 33, concept: 33, application: 34});
    const [difficulty, setDifficulty] = useState('medium'); // Default to Easy


    const handleGenerateQuestions = async () => {
        try{
            const ai_response = await aiService.generateQuestions({
                course_id: course.id,
                topics: topics,
            });
            console.log('ai_response', ai_response);
            if(ai_response.error){
                console.log('error', ai_response.error);
                toast.error(ai_response.error);
            } else {
                const questions = ai_response.map(question => ({
                    ...question,
                    id: uuidv4(),
                    source:'ai'
                }));
                setQuestions(prev => [...prev, ...questions]);
            }
        } catch(error) {
            console.error('Error generating new questions:', error);
        }
    }

    const handleRatioChange = (type, value) => {
        const newValue = value[0]; // Slider returns an array, we take the first value
        setRatios((prevRatios) => ({
            ...prevRatios,
            [type]: newValue,
        }));
    };

    const handleValueCommit = (type, value) => {
        const committedValue = value[0]; // Slider returns an array, we take the first value
        const remaining = 100 - committedValue;

        // Get the current sum of the remaining sliders
        const otherTypes = Object.keys(ratios).filter((key) => key !== type);
        const currentSum = otherTypes.reduce((sum, key) => sum + ratios[key], 0);

        // Calculate the proportional share for each remaining slider
        const updatedRatios = { ...ratios, [type]: committedValue };
        otherTypes.forEach((key) => {
            const ratio = currentSum === 0 ? 1 / otherTypes.length : ratios[key] / currentSum; // Handle edge case where currentSum is 0
            updatedRatios[key] = Math.round(ratio * remaining); // Distribute remaining proportionally
        });

        // Ensure the total adds up to exactly 100% (fix rounding errors)
        const total = Object.values(updatedRatios).reduce((sum, val) => sum + val, 0);
        if (total !== 100) {
            const adjustment = 100 - total;
            const lastKey = otherTypes[otherTypes.length - 1];
            updatedRatios[lastKey] += adjustment; // Adjust the last slider to fix rounding errors
        }

        setRatios(updatedRatios);
    };

    const handleOnComplete = () => {
        onComplete({
            difficulty, ratios
        });
    }



    return (
        <Flex direction="column" gap="4" maxWidth="600px" align="center">
            <Text size="1">Let's get the questions you are looking for</Text>
            <Flex align="center" gap="2">
                <Text size="1" weight="bold">Use Recommended</Text>
                <Switch
                    checked={useRecommended}
                    onCheckedChange={(checked) => setUseRecommended(checked)}
                />
            </Flex>
            {!useRecommended && (
                <Flex direction="column" gap="4">
                    <Text size="1" weight="bold">Difficulty</Text>
                    <RadioCards.Root value={difficulty} onValueChange={(value) => setDifficulty(value)} columns={{ initial: "1", sm: "3" }}>
                        <RadioCards.Item value="easy">
                            <Flex direction="column" width="100%">
                                <Text weight="bold">Easy</Text>
                            </Flex>
                        </RadioCards.Item>
                        <RadioCards.Item value="medium">
                            <Flex direction="column" width="100%">
                                <Text weight="bold">Medium</Text>
                            </Flex>
                        </RadioCards.Item>
                        <RadioCards.Item value="hard">
                            <Flex direction="column" width="100%">
                                <Text weight="bold">Hard</Text>
                            </Flex>
                        </RadioCards.Item>
                    </RadioCards.Root>
                    
                <Flex align="center" gap="2">
                    <Text size="1" weight="bold" as="p" style={{width:'200px'}}>Vocabulary({ratios.vocabulary}%)</Text>
                        <Slider
                            color={COMPETENCY_COLORS.vocabulary}
                            style={{width: '100%'}}
                            value={[ratios.vocabulary]}
                            onValueChange={(value) => handleRatioChange('vocabulary', value)}
                            onValueCommit={(value) => handleValueCommit('vocabulary', value)}
                        />
                </Flex> 
                <Flex align="center" gap="2">
                    <Text size="1" weight="bold" as="p" style={{width:'200px'}}>Concept({ratios.concept}%)</Text>
                    <Slider
                        color={COMPETENCY_COLORS.concept}
                        value={[ratios.concept]}
                        onValueChange={(value) => handleRatioChange('concept', value)}
                        onValueCommit={(value) => handleValueCommit('concept', value)}
                        min={0}
                        max={100}
                    />
                </Flex>
                <Flex align="center" gap="2">
                    <Text size="1" weight="bold" as="p" style={{width:'200px'}}>Application({ratios.application}%)</Text>
                    <Slider
                        color={COMPETENCY_COLORS.application}
                        value={[ratios.application]}
                        onValueChange={(value) => handleRatioChange('application', value)}
                        onValueCommit={(value) => handleValueCommit('application', value)}
                        min={0}
                        max={100}
                    />
                </Flex>
            </Flex>
            )}
            <Flex gap="4">
                <Button size="1" color="orange" onClick={handleOnComplete} disabled={loading.isLoading}>
                    {loading.isLoading ? 'Generating...' : 'Generate'}
                </Button>
                {/* <Button variant="soft" onClick={onCancel}>
                    Cancel
                </Button> */}
            </Flex>
        </Flex>
    )
}

export default AIQuestionsSettings;